import {
    collection, getDocs, doc, getDoc, addDoc, updateDoc,
    query, orderBy, where, limit, serverTimestamp
} from 'firebase/firestore';
import { db } from './firebase';

const COLLECTIONS = {
    PRODUCTS: 'products',
    MOVEMENTS: 'movements',
    EVENTS: 'events',
    SUPPLIERS: 'suppliers',
    RECIPES: 'recipes'
};

// ... existing services ...

export const supplierService = {
    getSuppliers: async () => {
        const q = query(collection(db, COLLECTIONS.SUPPLIERS), orderBy('name'));
        const querySnapshot = await getDocs(q);
        return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    },

    addSupplier: async (supplier) => {
        const docRef = await addDoc(collection(db, COLLECTIONS.SUPPLIERS), {
            ...supplier,
            createdAt: serverTimestamp()
        });
        return { id: docRef.id, ...supplier };
    },

    updateSupplier: async (id, updates) => {
        const docRef = doc(db, COLLECTIONS.SUPPLIERS, id);
        await updateDoc(docRef, updates);
        return { id, ...updates };
    },

    deleteSupplier: async (id) => {
        const docRef = doc(db, COLLECTIONS.SUPPLIERS, id);
        await import('firebase/firestore').then(({ deleteDoc }) => deleteDoc(docRef));
        return id;
    }
};

export const productService = {
    // --- Products ---
    getProducts: async () => {
        const q = query(collection(db, COLLECTIONS.PRODUCTS));
        const querySnapshot = await getDocs(q);
        return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    },

    getProduct: async (id) => {
        const docRef = doc(db, COLLECTIONS.PRODUCTS, id);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
            return { id: docSnap.id, ...docSnap.data() };
        }
        return null;
    },

    addProduct: async (product) => {
        const docRef = await addDoc(collection(db, COLLECTIONS.PRODUCTS), {
            ...product,
            stock: parseInt(product.stock || 0),
            createdAt: serverTimestamp()
        });
        return { id: docRef.id, ...product };
    },

    updateProduct: async (id, updates) => {
        const docRef = doc(db, COLLECTIONS.PRODUCTS, id);
        await updateDoc(docRef, updates);
        return { id, ...updates }; // optimistically return
    },

    deleteProduct: async (id) => {
        const docRef = doc(db, COLLECTIONS.PRODUCTS, id);
        await import('firebase/firestore').then(({ deleteDoc }) => deleteDoc(docRef));
        return id;
    },

    // --- Inventory Movements ---
    getMovements: async () => {
        // Order by date desc
        const q = query(collection(db, COLLECTIONS.MOVEMENTS), orderBy('date', 'desc'));
        const querySnapshot = await getDocs(q);
        return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    },

    addMovement: async (movement) => {
        // 1. Add movement record
        const movementData = {
            ...movement,
            date: new Date().toISOString()
        };
        const moveRef = await addDoc(collection(db, COLLECTIONS.MOVEMENTS), movementData);

        // 2. Update product stock
        const productRef = doc(db, COLLECTIONS.PRODUCTS, movement.productId);
        const productSnap = await getDoc(productRef);

        if (productSnap.exists()) {
            const currentStock = parseInt(productSnap.data().stock || 0);
            const delta = movement.type === 'out' ? -parseInt(movement.qty) : parseInt(movement.qty);
            await updateDoc(productRef, {
                stock: currentStock + delta
            });
        }

        return { id: moveRef.id, ...movementData };
    },

    reconcileProduct: async (productId, actualQty, currentSystemQty, userEmail) => {
        const delta = parseInt(actualQty) - parseInt(currentSystemQty);

        if (delta === 0) return null; // No change needed

        const type = delta > 0 ? 'in' : 'out';
        const qty = Math.abs(delta);

        const movement = {
            productId,
            type,
            qty,
            reason: delta > 0 ? 'audit_found' : 'audit_loss', // Special codes for audit
            notes: 'Stock Take Adjustment',
            user: userEmail,
            date: new Date().toISOString()
        };

        // Reuse existing addMovement logic to ensure consistency
        return await productService.addMovement(movement);
    },

    // --- Events ---
    getEvents: async () => {
        const q = query(collection(db, COLLECTIONS.EVENTS), orderBy('date', 'desc'));
        const querySnapshot = await getDocs(q);
        return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    },

    getEvent: async (id) => {
        const docRef = doc(db, COLLECTIONS.EVENTS, id);
        const docSnap = await getDoc(docRef);
        return docSnap.exists() ? { id: docSnap.id, ...docSnap.data() } : null;
    },

    addEvent: async (event) => {
        const docRef = await addDoc(collection(db, COLLECTIONS.EVENTS), {
            ...event,
            createdAt: serverTimestamp(),
            revenue: parseInt(event.revenue || 0)
        });
        return { id: docRef.id, ...event };
    },

    updateEvent: async (id, updates) => {
        const docRef = doc(db, COLLECTIONS.EVENTS, id);
        await updateDoc(docRef, updates);
        return { id, ...updates };
    },

    // --- Dashboard Stats ---
    getDashboardStats: async () => {
        const [products, events, movements, salesLineItems] = await Promise.all([
            productService.getProducts(),
            productService.getEvents(),
            // Fetch recent movements for activity feed
            getDocs(query(collection(db, COLLECTIONS.MOVEMENTS), orderBy('date', 'desc'), limit(5))).then(s => s.docs.map(d => ({ id: d.id, ...d.data(), type: 'movement' }))),
            // Fetch all sales line items to calculate revenue
            getDocs(collection(db, 'sales_line_items')).then(s => s.docs.map(d => ({ id: d.id, ...d.data() })))
        ]);

        console.log('[getDashboardStats] Products count:', products.length);
        console.log('[getDashboardStats] Events count:', events.length);
        console.log('[getDashboardStats] SalesLineItems count:', salesLineItems.length);

        // 1. Basic KPIs
        const lowStock = products.filter(p => p.stock < (p.lowStockThreshold || 10)).length;
        const totalStockValue = products.reduce((acc, p) => acc + ((p.stock || 0) * (p.cost || 0)), 0);
        const completedEvents = events.filter(e => new Date(e.date) < new Date()).length;

        // Calculate total revenue
        const revenueTotal = salesLineItems.reduce((acc, item) => {
            const quantity = Number(item.quantity) || 0;
            const price = Number(item.price) || 0;
            return acc + (quantity * price);
        }, 0);
        console.log('[getDashboardStats] Total Revenue:', revenueTotal);

        // 2. Group by Event
        const eventRevenueMap = {};
        salesLineItems.forEach(item => {
            const eventId = item.eventId || 'no-event';
            const eventName = item.eventName || 'Sans événement';
            const eventDate = item.eventDate || item.timestamp;

            if (!eventRevenueMap[eventId]) {
                eventRevenueMap[eventId] = {
                    eventId: eventId,
                    name: eventName,
                    date: eventDate,
                    revenue: 0,
                    transactionCount: 0
                };
            }

            const quantity = Number(item.quantity) || 0;
            const price = Number(item.price) || 0;

            eventRevenueMap[eventId].revenue += (quantity * price);
            eventRevenueMap[eventId].transactionCount += 1;
        });

        // Last event revenue (most recent)
        const lastEventRevenue = Object.values(eventRevenueMap)
            .sort((a, b) => new Date(b.date) - new Date(a.date))[0] || null;

        // 3. Annual Revenue (current year)
        const currentYear = new Date().getFullYear();
        const yearRevenue = salesLineItems
            .filter(item => {
                const itemDate = new Date(item.timestamp || item.eventDate);
                return itemDate.getFullYear() === currentYear;
            })
            .reduce((sum, item) => {
                const quantity = Number(item.quantity) || 0;
                const price = Number(item.price) || 0;
                return sum + (quantity * price);
            }, 0);

        // 4. Average Basket
        const totalTransactions = salesLineItems.length;
        const avgBasket = totalTransactions > 0 ? revenueTotal / totalTransactions : 0;

        // 5. Top Products of the Month
        const currentMonth = new Date().getMonth();
        const currentMonthYear = new Date().getFullYear();
        const monthSales = {};

        salesLineItems
            .filter(item => {
                const itemDate = new Date(item.timestamp || item.eventDate);
                return itemDate.getMonth() === currentMonth && itemDate.getFullYear() === currentMonthYear;
            })
            .forEach(item => {
                const productName = item.productName || 'Unknown';
                if (!monthSales[productName]) {
                    monthSales[productName] = { qty: 0, revenue: 0 };
                }
                const quantity = Number(item.quantity) || 0;
                const price = Number(item.price) || 0;
                monthSales[productName].qty += quantity;
                monthSales[productName].revenue += quantity * price;
            });

        const topProducts = Object.entries(monthSales)
            .map(([name, data]) => ({ name, ...data }))
            .sort((a, b) => b.revenue - a.revenue)
            .slice(0, 3);

        // 6. Upcoming Event (next event in the future)
        const upcomingEvent = events
            .filter(e => new Date(e.date) > new Date())
            .sort((a, b) => new Date(a.date) - new Date(b.date))[0] || null;

        return {
            lowStock,
            totalStockValue,
            completedEvents,
            revenueTotal,
            lastEventRevenue,
            yearRevenue,
            avgBasket,
            topProducts,
            upcomingEvent
        };
    },

    // --- Imports & Sales Data ---
    getImports: async () => {
        const q = query(collection(db, 'sales_imports'), orderBy('date', 'desc'));
        const querySnapshot = await getDocs(q);
        return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    },

    addSalesImport: async (importData) => {
        const docRef = await addDoc(collection(db, 'sales_imports'), {
            ...importData,
            createdAt: serverTimestamp()
        });
        return { id: docRef.id, ...importData };
    },

    addSalesLineItem: async (lineItem) => {
        return await addDoc(collection(db, 'sales_line_items'), lineItem);
    },

    getSalesByEvent: async (eventId) => {
        const q = query(
            collection(db, 'sales_line_items'),
            where('eventId', '==', eventId)
        );
        const snapshot = await getDocs(q);
        return snapshot.docs.map(d => d.data());
    },

    deleteSalesImport: async (id) => {
        // 1. Delete the import record
        const docRef = doc(db, 'sales_imports', id);
        await import('firebase/firestore').then(({ deleteDoc }) => deleteDoc(docRef));

        // 2. Delete associated line items (cleanup)
        // Note: This does NOT revert stock changes (out of scope for now)
        const q = query(collection(db, 'sales_line_items'), where('importId', '==', id));
        const snapshot = await getDocs(q);

        const deletePromises = snapshot.docs.map(d =>
            import('firebase/firestore').then(({ deleteDoc }) => deleteDoc(d.ref))
        );
        await Promise.all(deletePromises);

        return id;
    },

    deleteEvent: async (id) => {
        const docRef = doc(db, COLLECTIONS.EVENTS, id);
        await import('firebase/firestore').then(({ deleteDoc }) => deleteDoc(docRef));
        return id;
    },

    // --- UTILITIES ---
    async fixEncodings() {
        console.log('Starting encoding fix...');
        const replacements = {
            'Ã©': 'é', 'Ã¨': 'è', 'Ã ': 'à', 'Ã¢': 'â',
            'Ãª': 'ê', 'Ã®': 'î', 'Ã´': 'ô', 'Ã»': 'û',
            'Ã«': 'ë', 'Ã¯': 'ï', 'Ã¼': 'ü', 'Ã§': 'ç',
            'Ã¤': 'ä', 'A¨': 'è', 'A©': 'é'
        };

        const fixString = (str) => {
            if (!str) return str;
            let newStr = str;
            Object.entries(replacements).forEach(([bad, good]) => {
                newStr = newStr.replace(new RegExp(bad, 'g'), good);
            });
            // Specific fix for "JÃ¤ger" if missed
            if (newStr.includes('Ã¤')) newStr = newStr.replace(/Ã¤/g, 'ä');
            return newStr;
        };

        let fixedCount = 0;

        // 1. Fix Products
        const productsSnap = await getDocs(collection(db, COLLECTIONS.PRODUCTS));
        const productUpdates = productsSnap.docs.map(async (docSnap) => {
            const data = docSnap.data();
            const newName = fixString(data.name);
            const newBrand = fixString(data.brand);

            if (newName !== data.name || newBrand !== data.brand) {
                fixedCount++;
                return updateDoc(doc(db, COLLECTIONS.PRODUCTS, docSnap.id), {
                    name: newName,
                    brand: newBrand
                });
            }
        });
        await Promise.all(productUpdates);

        // 2. Fix Sales Line Items
        const salesSnap = await getDocs(collection(db, 'sales_line_items'));
        const salesUpdates = salesSnap.docs.map(async (docSnap) => {
            const data = docSnap.data();
            const newName = fixString(data.productName);

            if (newName !== data.productName) {
                fixedCount++;
                return updateDoc(doc(db, 'sales_line_items', docSnap.id), {
                    productName: newName
                });
            }
        });
        await Promise.all(salesUpdates);

        console.log(`Fixed encodings for ${fixedCount} items.`);
        return fixedCount;
    }
};

// Helper: Deduct ingredients when a recipe-linked product is sold
export const deductRecipeIngredients = async (recipeId, qtySold) => {
    // 1. Fetch the recipe directly
    const recipeRef = doc(db, COLLECTIONS.RECIPES, recipeId);
    const recipeSnap = await getDoc(recipeRef);
    if (!recipeSnap.exists()) return;

    const recipe = recipeSnap.data();
    if (!recipe.ingredients || recipe.ingredients.length === 0) return;

    // 2. Helper: Normalize to ml
    const normalizeToMl = (qty, unit) => {
        const val = parseFloat(qty || 0);
        switch (unit?.toLowerCase()) {
            case 'l': return val * 1000;
            case 'cl': return val * 10;
            case 'ml': return val;
            case 'kg': return val * 1000; // Treat as grams for simplicity
            case 'g': return val;
            default: return val; // Unit/Piece
        }
    };

    // 3. Process each ingredient
    const deductionPromises = recipe.ingredients.map(async (ingredient) => {
        const ingredientProduct = await productService.getProduct(ingredient.productId);
        if (!ingredientProduct) return;

        // Calculate total needed in base units
        const qtyPerServing = normalizeToMl(ingredient.qty, ingredient.unit);
        const totalNeeded = qtyPerServing * qtySold;

        // Convert to product's unit
        const productVolumeInMl = normalizeToMl(
            ingredientProduct.volumeAmount || 1,
            ingredientProduct.volumeUnit || 'unit'
        );

        const unitsToDeduct = totalNeeded / productVolumeInMl;

        // Create movement
        if (unitsToDeduct > 0) {
            await productService.addMovement({
                productId: ingredient.productId,
                type: 'out',
                qty: Math.ceil(unitsToDeduct), // Round up to nearest unit
                reason: 'recipe_deduction',
                notes: `Auto-deducted for ${qtySold}x ${recipe.name}`,
                user: 'system',
                date: new Date().toISOString()
            });
        }
    });

    await Promise.all(deductionPromises);
};

export const recipeService = {
    getRecipes: async () => {
        const q = query(collection(db, COLLECTIONS.RECIPES), orderBy('name'));
        const querySnapshot = await getDocs(q);
        return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    },

    addRecipe: async (recipe) => {
        const docRef = await addDoc(collection(db, COLLECTIONS.RECIPES), {
            ...recipe,
            createdAt: serverTimestamp()
        });
        return { id: docRef.id, ...recipe };
    },

    updateRecipe: async (id, updates) => {
        const docRef = doc(db, COLLECTIONS.RECIPES, id);
        await updateDoc(docRef, updates);
        return { id, ...updates };
    },

    deleteRecipe: async (id) => {
        const docRef = doc(db, COLLECTIONS.RECIPES, id);
        await import('firebase/firestore').then(({ deleteDoc }) => deleteDoc(docRef));
        return id;
    }
};

export const userService = {
    getUsers: async () => {
        // In a real app, you might sync Auth users to a Firestore 'users' collection
        // For MVP, we'll assume a 'users' collection exists
        const q = query(collection(db, 'users'), orderBy('email'));
        const querySnapshot = await getDocs(q);
        return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    },

    updateUserRole: async (id, role) => {
        const docRef = doc(db, 'users', id);
        await updateDoc(docRef, { role });
        return { id, role };
    },

    // Create user profile in Firestore after Auth signup
    createUserProfile: async (uid, email, role = 'staff') => {
        const docRef = doc(db, 'users', uid);
        const userData = {
            email,
            role,
            createdAt: serverTimestamp()
        };
        // Use setDoc to define the ID as the Auth UID
        await import('firebase/firestore').then(({ setDoc }) => setDoc(docRef, userData));
        return { id: uid, ...userData };
    },

    getUserProfile: async (uid) => {
        const docRef = doc(db, 'users', uid);
        const docSnap = await getDoc(docRef);
        return docSnap.exists() ? { id: docSnap.id, ...docSnap.data() } : null;
    },

    deleteUser: async (id) => {
        const docRef = doc(db, 'users', id);
        await import('firebase/firestore').then(({ deleteDoc }) => deleteDoc(docRef));
        return id;
    }
};

export const resetService = {
    clearDatabase: async () => {
        const collectionsToClear = [
            COLLECTIONS.PRODUCTS,
            COLLECTIONS.MOVEMENTS,
            COLLECTIONS.EVENTS,
            COLLECTIONS.SUPPLIERS,
            'sales_imports',
            'sales_line_items'
        ];

        const { deleteDoc, getDocs, collection } = await import('firebase/firestore');

        for (const colName of collectionsToClear) {
            const q = query(collection(db, colName));
            const snapshot = await getDocs(q);
            const deletePromises = snapshot.docs.map(doc => deleteDoc(doc.ref));
            await Promise.all(deletePromises);
        }
        return true;
    }
};
